import React, { useState, useEffect, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import firebase from "../../firebase";
import { getVendorId } from "../../utils/rbac";
import "./vendoranalytics.css";

const db = () => firebase.firestore();

// ── Helpers ───────────────────────────────────────────────────────────────
const MONTHS = ["January","February","March","April","May","June",
                 "July","August","September","October","November","December"];
const SHORT_MONTHS = ["Jan","Feb","Mar","Apr","May","Jun",
                      "Jul","Aug","Sep","Oct","Nov","Dec"];

const STATUS_COLORS = {
  Completed: "#43a047",
  Accepted:  "#1976d2",
  Pending:   "#ff9800",
  Cancelled: "#e53935",
  Canceled:  "#e53935",
};

const toRupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const monthKey      = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const prevMonth     = (d) => new Date(d.getFullYear(), d.getMonth() - 1, 1);
const nextMonth     = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 1);
const isCurrentMonth = (d) => {
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth();
};

const mergeDocs = (...maps) => {
  const merged = {};
  maps.forEach((m) => Object.assign(merged, m));
  return Object.values(merged);
};

const todayDateStr = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
};

const fromDateStr = (range) => {
  const n = new Date();
  if (range === "7days") {
    const d = new Date(n); d.setDate(d.getDate() - 7);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }
  if (range === "30days") {
    const d = new Date(n); d.setDate(d.getDate() - 30);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }
  return `${n.getFullYear()}-01-01`;
};

const fmtBusiest = (ds) => {
  if (!ds) return "—";
  const dt = new Date(ds + "T12:00:00");
  return `${["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][dt.getDay()]} ${dt.getDate()} ${SHORT_MONTHS[dt.getMonth()]}`;
};

// ── SVG Circular Progress ─────────────────────────────────────────────────
const CircularProgress = ({ pct, size = 80, sw = 8 }) => {
  const r = (size - sw) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke="rgba(255,255,255,.25)" strokeWidth={sw} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke="white" strokeWidth={sw}
        strokeDasharray={c} strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`} />
    </svg>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// TAB 1 — Dashboard  (mirrors dashboard.dart)
// ═══════════════════════════════════════════════════════════════════════════
const DashboardTab = ({ vendorId }) => {
  const [range,   setRange]   = useState("7days");
  const [allDocs, setAllDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!vendorId) return;
    setLoading(true);
    db().collection("appointments")
      .where("vendorId", "==", vendorId)
      .get()
      .then((snap) => {
        setAllDocs(snap.docs.map((d) => ({ _id: d.id, ...d.data() })));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [vendorId]);

  const stats = useMemo(() => {
    const fromDate = fromDateStr(range);
    const today    = todayDateStr();
    const inRange  = allDocs.filter((b) => (b.scheduleDate || "") >= fromDate);
    const completed = inRange.filter((b) => b.status === "Completed");
    const cancelled = inRange.filter((b) => b.status === "Cancelled" || b.status === "Canceled");
    const upcoming  = allDocs.filter((b) =>
      (b.status === "Accepted" || b.status === "Pending") && (b.scheduleDate || "") >= today
    );

    const totalRev = completed.reduce((s, b) => s + parseFloat(b.finalPrice || 0), 0);
    const custSet  = new Set(completed.map((b) => b.userId || b.customerId).filter(Boolean));
    const avgOrder = completed.length ? totalRev / completed.length : 0;

    const todayComp = completed.filter((b) => b.scheduleDate === today);
    const todayRev  = todayComp.reduce((s, b) => s + parseFloat(b.finalPrice || 0), 0);

    // Busiest day by revenue
    const dayRevMap = {};
    completed.forEach((b) => {
      dayRevMap[b.scheduleDate] = (dayRevMap[b.scheduleDate] || 0) + parseFloat(b.finalPrice || 0);
    });
    const bEntry = Object.entries(dayRevMap).sort((a, b) => b[1] - a[1])[0];

    // Top 5 services
    const svcMap = {};
    completed.forEach((b) => {
      (b.services || []).forEach((s) => {
        const nm = s.name || s.serviceName || "";
        if (nm) svcMap[nm] = (svcMap[nm] || 0) + 1;
      });
      if (!(b.services || []).length) {
        const sn = b.serviceName || b.service || "";
        if (sn) svcMap[sn] = (svcMap[sn] || 0) + 1;
      }
    });
    const topServices = Object.entries(svcMap).sort((a, b) => b[1] - a[1]).slice(0, 5);

    // Chart data (group by day or month)
    let chartLabels = [], chartRevenue = [], chartCount = [];
    const DN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    if (range === "year") {
      const mMap = {};
      completed.forEach((b) => {
        const mk = (b.scheduleDate || "").substring(0, 7);
        if (!mMap[mk]) mMap[mk] = { revenue: 0, count: 0 };
        mMap[mk].revenue += parseFloat(b.finalPrice || 0);
        mMap[mk].count   += 1;
      });
      const keys = Object.keys(mMap).sort();
      chartLabels  = keys.map((k) => {
        const [y, m] = k.split("-");
        return SHORT_MONTHS[parseInt(m) - 1] + " " + y;
      });
      chartRevenue = keys.map((k) => mMap[k].revenue);
      chartCount   = keys.map((k) => mMap[k].count);
    } else {
      const dMap = {};
      completed.forEach((b) => {
        const sd = b.scheduleDate || "";
        if (!dMap[sd]) dMap[sd] = { revenue: 0, count: 0 };
        dMap[sd].revenue += parseFloat(b.finalPrice || 0);
        dMap[sd].count   += 1;
      });
      const keys = Object.keys(dMap).sort();
      chartLabels  = keys.map((k) => {
        const dt = new Date(k + "T12:00:00");
        return DN[dt.getDay()] + " " + String(dt.getDate()).padStart(2, "0");
      });
      chartRevenue = keys.map((k) => dMap[k].revenue);
      chartCount   = keys.map((k) => dMap[k].count);
    }

    // Recent 5 appointments (any status, sorted by date desc)
    const recent = [...allDocs]
      .sort((a, b) => (b.scheduleDate || "").localeCompare(a.scheduleDate || ""))
      .slice(0, 5);

    return {
      completed:       completed.length,
      cancelled:       cancelled.length,
      upcoming:        upcoming.length,
      totalRev,
      uniqueCustomers: custSet.size,
      avgOrder,
      todayCompleted:  todayComp.length,
      todayRev,
      busiestDay:      bEntry?.[0] || null,
      topServices,
      chartLabels,
      chartRevenue,
      chartCount,
      recent,
      completionRate:  (completed.length + cancelled.length) > 0
        ? Math.round((completed.length / (completed.length + cancelled.length)) * 100)
        : 0,
    };
  }, [allDocs, range]);

  if (loading) return (
    <div className="va_loading"><i className="fa fa-spinner fa-pulse fa-2x"></i></div>
  );

  const RANGE_LABEL = { "7days": "Last 7 Days", "30days": "Last 30 Days", "year": "This Year" };
  const SVC_COLORS  = ["#ff9800", "#ff5500", "#8b5cf6", "#1976d2", "#43a047"];

  return (
    <div>
      {/* ── Range selector ── */}
      <div className="va_range_selector">
        {[["7days", "7 Days"], ["30days", "30 Days"], ["year", "This Year"]].map(([k, l]) => (
          <button
            key={k}
            className={`va_range_btn${range === k ? " active" : ""}`}
            onClick={() => setRange(k)}
          >{l}</button>
        ))}
      </div>

      {/* ── Today banner ── */}
      <div className="va_today_banner">
        <div className="va_today_left">
          <div className="va_today_icon"><i className="fa fa-sun-o"></i></div>
          <div>
            <div className="va_today_title">Today's Summary</div>
            <div className="va_today_sub">
              {stats.todayCompleted} appointment{stats.todayCompleted !== 1 ? "s" : ""}&nbsp;&middot;&nbsp;{toRupee(stats.todayRev)} earned
            </div>
          </div>
        </div>
        {stats.upcoming > 0 && (
          <div className="va_upcoming_pill">
            <i className="fa fa-clock-o"></i>&nbsp;{stats.upcoming} upcoming
          </div>
        )}
      </div>

      {/* ── 2 Big KPIs ── */}
      <div className="va_kpi_grid">
        <div className="va_kpi_big">
          <div className="va_kpi_icon" style={{ background: "rgba(255,152,0,.12)" }}>
            <i className="fa fa-inr" style={{ color: "#ff9800" }}></i>
          </div>
          <div className="va_kpi_val">{toRupee(stats.totalRev)}</div>
          <div className="va_kpi_lbl">Total Revenue</div>
          <div className="va_kpi_range">{RANGE_LABEL[range]}</div>
        </div>
        <div className="va_kpi_big">
          <div className="va_kpi_icon" style={{ background: "rgba(25,118,210,.12)" }}>
            <i className="fa fa-users" style={{ color: "#1976d2" }}></i>
          </div>
          <div className="va_kpi_val">{stats.uniqueCustomers}</div>
          <div className="va_kpi_lbl">Unique Customers</div>
          <div className="va_kpi_range">{RANGE_LABEL[range]}</div>
        </div>
      </div>

      {/* ── 4 Small KPIs ── */}
      <div className="va_kpi_small_grid">
        {[
          { ic: "fa-check-circle", val: stats.completed,                     lbl: "Completed", col: "#43a047" },
          { ic: "fa-times-circle", val: stats.cancelled,                     lbl: "Cancelled", col: "#e53935" },
          { ic: "fa-line-chart",   val: toRupee(Math.round(stats.avgOrder)), lbl: "Avg Order", col: "#8b5cf6" },
          { ic: "fa-fire",         val: fmtBusiest(stats.busiestDay),        lbl: "Peak Day",  col: "#ff9800" },
        ].map((s) => (
          <div key={s.lbl} className="va_kpi_small">
            <i className={`fa ${s.ic}`} style={{ color: s.col, fontSize: 18 }}></i>
            <div className="va_kpi_small_val" style={{ color: s.col }}>{s.val}</div>
            <div className="va_kpi_small_lbl">{s.lbl}</div>
          </div>
        ))}
      </div>

      {/* ── Revenue Trend chart (Bar + Line combo) ── */}
      {stats.chartLabels.length > 0 && (
        <div className="va_card">
          <div className="va_section_header">
            <i className="fa fa-bar-chart"></i> Revenue Trend &middot; {RANGE_LABEL[range]}
          </div>
          <div className="va_chart_wrap">
            <Bar
              data={{
                labels: stats.chartLabels,
                datasets: [
                  {
                    type: "bar",
                    label: "Revenue (₹)",
                    data: stats.chartRevenue,
                    backgroundColor: "rgba(255,152,0,0.72)",
                    yAxisID: "revenue",
                  },
                  {
                    type: "line",
                    label: "Appointments",
                    data: stats.chartCount,
                    borderColor: "#1976d2",
                    backgroundColor: "transparent",
                    borderWidth: 2,
                    pointBackgroundColor: "#1976d2",
                    pointRadius: 3,
                    fill: false,
                    yAxisID: "count",
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                legend: { position: "bottom", labels: { fontSize: 11, boxWidth: 12 } },
                scales: {
                  xAxes: [{ ticks: { fontSize: 10 }, gridLines: { display: false } }],
                  yAxes: [
                    {
                      id: "revenue", position: "left",
                      ticks: {
                        fontSize: 10,
                        callback: (v) => v >= 1000 ? "₹" + (v / 1000).toFixed(0) + "k" : "₹" + v,
                      },
                      gridLines: { color: "rgba(0,0,0,.05)" },
                    },
                    {
                      id: "count", position: "right",
                      ticks: {
                        fontSize: 10, stepSize: 1,
                        callback: (v) => Number.isInteger(v) ? v : "",
                      },
                      gridLines: { display: false },
                    },
                  ],
                },
                tooltips: { mode: "index", intersect: false },
              }}
            />
          </div>
        </div>
      )}

      {/* ── Appointment Breakdown ── */}
      <div className="va_card">
        <div className="va_section_header">
          <i className="fa fa-pie-chart"></i> Appointment Breakdown
        </div>
        {(() => {
          const total = stats.completed + stats.cancelled + stats.upcoming;
          if (!total) return <div className="va_empty">No appointments in this period.</div>;
          return [
            { label: "Completed", count: stats.completed, col: "#43a047" },
            { label: "Upcoming",  count: stats.upcoming,  col: "#1976d2" },
            { label: "Cancelled", count: stats.cancelled, col: "#e53935" },
          ].map(({ label, count, col }) => (
            <div key={label} className="va_funnel_row">
              <div className="va_funnel_label">{label}</div>
              <div className="va_funnel_bar_wrap">
                <div className="va_funnel_bar"
                  style={{ width: total ? `${(count / total) * 100}%` : "0%", background: col }}>
                </div>
              </div>
              <div className="va_funnel_count" style={{ color: col }}>{count}</div>
              <div className="va_funnel_pct">{total ? Math.round((count / total) * 100) : 0}%</div>
            </div>
          ));
        })()}
      </div>

      {/* ── Top 5 Services ── */}
      {stats.topServices.length > 0 && (
        <div className="va_card">
          <div className="va_section_header">
            <i className="fa fa-scissors"></i> Top Services
          </div>
          {(() => {
            const max = stats.topServices[0]?.[1] || 1;
            return stats.topServices.map(([name, count], i) => (
              <div key={name} className="va_service_row">
                <div className="va_service_rank" style={{ background: SVC_COLORS[i] }}>{i + 1}</div>
                <div className="va_service_name">{name}</div>
                <div className="va_service_bar_wrap">
                  <div className="va_service_bar"
                    style={{ width: `${(count / max) * 100}%`, background: SVC_COLORS[i] }}>
                  </div>
                </div>
                <div className="va_service_count">{count}x</div>
              </div>
            ));
          })()}
        </div>
      )}

      {/* ── Completion Rate ── */}
      <div className="va_completion_card">
        <div className="va_completion_left">
          <div className="va_completion_label">Completion Rate</div>
          <div className="va_completion_pct">{stats.completionRate}%</div>
          <div className="va_completion_sub">
            {stats.completed} completed &middot; {stats.cancelled} cancelled
          </div>
          <div className="va_completion_badge">
            {stats.completionRate >= 80 ? "Excellent"
              : stats.completionRate >= 60 ? "Good"
              : "Needs Improvement"}
          </div>
        </div>
        <div className="va_completion_right">
          <div style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress pct={stats.completionRate} size={80} sw={8} />
            <span className="va_completion_pct_sm">{stats.completionRate}%</span>
          </div>
        </div>
      </div>

      {/* ── Recent Appointments ── */}
      {stats.recent.length > 0 && (
        <div className="va_card">
          <div className="va_section_header">
            <i className="fa fa-history"></i> Recent Appointments
          </div>
          <div className="va_recent_list">
            {stats.recent.map((b, i) => {
              const col = STATUS_COLORS[b.status] || "#9e9e9e";
              return (
                <div
                  key={b._id || i}
                  className="va_recent_row"
                  style={i < stats.recent.length - 1 ? { borderBottom: "1px solid #f5f5f5" } : {}}
                >
                  <div className="va_recent_avatar" style={{ background: col + "22", color: col }}>
                    {(b.userName || "?")[0].toUpperCase()}
                  </div>
                  <div className="va_recent_info">
                    <div className="va_recent_name">{b.userName || "—"}</div>
                    <div className="va_recent_detail">
                      {b.scheduleDate}{b.scheduleTime ? " · " + b.scheduleTime : ""}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div className="va_recent_amount">{toRupee(b.finalPrice || 0)}</div>
                    <div className="va_recent_status" style={{ color: col }}>&bull; {b.status}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// TAB 2 — Daily Revenue
// ═══════════════════════════════════════════════════════════════════════════
const DailyRevenueTab = ({ vendorId }) => {
  const [month, setMonth]     = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [allDocs, setAllDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!vendorId) return;
    setLoading(true);
    db().collection("appointments")
      .where("vendorId", "==", vendorId)
      .where("status", "==", "Completed")
      .get()
      .then((snap) => {
        setAllDocs(snap.docs.map((d) => ({ _id: d.id, ...d.data() })));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [vendorId]);

  const dayStats = useMemo(() => {
    const mk  = monthKey(month);
    const map = {};
    allDocs.forEach((b) => {
      const sd = (b.scheduleDate || "").substring(0, 10);
      if (!sd.startsWith(mk)) return;
      if (!map[sd]) map[sd] = { revenue: 0, count: 0 };
      map[sd].revenue += parseFloat(b.finalPrice || 0);
      map[sd].count   += 1;
    });
    return Object.entries(map)
      .map(([date, s]) => ({ date, ...s }))
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [allDocs, month]);

  const totalRev = dayStats.reduce((s, d) => s + d.revenue, 0);
  const totalCnt = dayStats.reduce((s, d) => s + d.count, 0);
  const maxRev   = dayStats.reduce((m, d) => Math.max(m, d.revenue), 0);
  const isCur    = isCurrentMonth(month);

  const fmtDay = (dateStr) => {
    const dt = new Date(dateStr + "T12:00:00");
    const days = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
    return `${String(dt.getDate()).padStart(2,"0")} ${days[dt.getDay()]}`;
  };

  return (
    <div>
      <div className="va_card">
        <div className="va_month_nav">
          <button className="va_nav_btn" onClick={() => setMonth(prevMonth(month))}>
            <i className="fa fa-chevron-left"></i>
          </button>
          <span className="va_month_label">
            {MONTHS[month.getMonth()]} {month.getFullYear()}
          </span>
          {isCur && <span className="va_this_month_pill">This Month</span>}
          <button className="va_nav_btn" onClick={() => setMonth(nextMonth(month))} disabled={isCur}>
            <i className="fa fa-chevron-right"></i>
          </button>
        </div>

        <div className="va_stats_row">
          {[
            { ic: "fa-inr",         val: toRupee(totalRev), lbl: "Total Revenue", bg: "rgba(255,152,0,.1)",  col: "#ff9800" },
            { ic: "fa-check-circle",val: totalCnt,          lbl: "Appointments",  bg: "rgba(67,160,71,.1)",  col: "#43a047" },
            { ic: "fa-calendar",    val: dayStats.length,   lbl: "Active Days",   bg: "rgba(25,118,210,.1)", col: "#1976d2" },
            { ic: "fa-line-chart",  val: dayStats.length ? toRupee(Math.round(totalRev / dayStats.length)) : "—",
              lbl: "Avg / Day", bg: "rgba(123,31,162,.1)", col: "#7b1fa2" },
          ].map((s) => (
            <div key={s.lbl} className="va_stat_tile" style={{ background: s.bg }}>
              <i className={`fa ${s.ic}`} style={{ color: s.col, fontSize: 18 }}></i>
              <div className="va_stat_val" style={{ color: s.col }}>{s.val}</div>
              <div className="va_stat_lbl">{s.lbl}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="va_card">
        <div className="va_section_header">
          <i className="fa fa-calendar-check-o"></i> Day-wise Revenue
        </div>
        {loading ? (
          <div className="va_loading"><i className="fa fa-spinner fa-pulse"></i></div>
        ) : dayStats.length === 0 ? (
          <div className="va_empty">No completed appointments in {MONTHS[month.getMonth()]}.</div>
        ) : (
          <div className="va_day_list">
            {dayStats.map(({ date, revenue, count }) => (
              <div key={date} className="va_day_row">
                <div className="va_day_label">{fmtDay(date)}</div>
                <div className="va_day_bar_wrap">
                  <div className="va_day_bar"
                    style={{ width: maxRev > 0 ? `${(revenue / maxRev) * 100}%` : "4px" }}>
                  </div>
                </div>
                <div className="va_day_count">{count} appt{count !== 1 ? "s" : ""}</div>
                <div className="va_day_amount">{toRupee(revenue)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// TAB 3 — Stylist Analytics
// ═══════════════════════════════════════════════════════════════════════════
const StylistPerformanceCard = ({ stylist, vendorId }) => {
  const [month, setMonth]     = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [docs1, setDocs1]     = useState({});
  const [docs2, setDocs2]     = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const mk   = monthKey(month);
    const from = `${mk}-01`;
    const nextM = nextMonth(month);
    const to   = `${nextM.getFullYear()}-${String(nextM.getMonth() + 1).padStart(2,"0")}-01`;

    setLoading(true);
    setDocs1({});
    setDocs2({});

    const base = db().collection("appointments")
      .where("status",       "==", "Completed")
      .where("scheduleDate", ">=", from)
      .where("scheduleDate", "<",  to);

    let d1Done = false, d2Done = false;
    const check = () => { if (d1Done && d2Done) setLoading(false); };

    base.where("stylistId", "==", stylist.id).get().then((snap) => {
      const m = {};
      snap.docs.forEach((d) => (m[d.id] = { _id: d.id, ...d.data() }));
      setDocs1(m);
      d1Done = true; check();
    }).catch(() => { d1Done = true; check(); });

    base.where("stylistIds", "array-contains", stylist.id).get().then((snap) => {
      const m = {};
      snap.docs.forEach((d) => (m[d.id] = { _id: d.id, ...d.data() }));
      setDocs2(m);
      d2Done = true; check();
    }).catch(() => { d2Done = true; check(); });
  }, [stylist.id, month]);

  const allDocs = useMemo(() => mergeDocs(docs1, docs2), [docs1, docs2]);

  const { bookings, services, revenue } = useMemo(() => {
    let bookings = 0, services = 0, revenue = 0;
    allDocs.forEach((b) => {
      bookings++;
      const breakdown = b.stylistBreakdown?.[stylist.id];
      if (breakdown) {
        revenue  += parseFloat(breakdown.revenue    || 0);
        services += parseInt(breakdown.serviceCount || 0, 10);
      } else {
        revenue  += parseFloat(b.finalPrice || 0);
        services += (b.services || []).length;
      }
    });
    return { bookings, services, revenue };
  }, [allDocs, stylist.id]);

  const isCur    = isCurrentMonth(month);
  const initials = (stylist.name || "?")[0].toUpperCase();

  return (
    <div className="va_card va_stylist_card">
      <div className="va_stylist_header">
        <div className="va_stylist_avatar">
          {stylist.image ? <img src={stylist.image} alt={stylist.name} /> : initials}
        </div>
        <div>
          <div className="va_stylist_name">{stylist.name}</div>
          <div className="va_stylist_role">{stylist.role || "Stylist"}</div>
        </div>
        <span className={stylist.active ? "va_active_pill" : "va_inactive_pill"}>
          {stylist.active ? "Active" : "Inactive"}
        </span>
      </div>

      <div className="va_month_nav" style={{ marginBottom: 12 }}>
        <button className="va_nav_btn" onClick={() => setMonth(prevMonth(month))}>
          <i className="fa fa-chevron-left"></i>
        </button>
        <span className="va_month_label">
          {SHORT_MONTHS[month.getMonth()]} {month.getFullYear()}
        </span>
        {isCur && <span className="va_this_month_pill">This Month</span>}
        <button className="va_nav_btn" onClick={() => setMonth(nextMonth(month))} disabled={isCur}>
          <i className="fa fa-chevron-right"></i>
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "16px 0", color: "#bdbdbd" }}>
          <i className="fa fa-spinner fa-pulse"></i>
        </div>
      ) : (
        <div className="va_mini_metrics">
          {[
            { ic: "fa-check-square", val: bookings,        lbl: "Bookings", bg: "rgba(59,130,246,.08)",  col: "#3b82f6" },
            { ic: "fa-scissors",     val: services,         lbl: "Services", bg: "rgba(139,92,246,.08)",  col: "#8b5cf6" },
            { ic: "fa-inr",          val: toRupee(revenue), lbl: "Revenue",  bg: "rgba(16,185,129,.08)",  col: "#10b981" },
          ].map((m) => (
            <div key={m.lbl} className="va_mini_tile" style={{ background: m.bg }}>
              <i className={`fa ${m.ic}`} style={{ color: m.col, fontSize: 15 }}></i>
              <div className="va_mini_val" style={{ color: m.col }}>{m.val}</div>
              <div className="va_mini_lbl">{m.lbl}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const StylistOverviewChart = ({ vendorId }) => {
  const [docs, setDocs]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!vendorId) return;
    db().collection("appointments")
      .where("vendorId", "==", vendorId)
      .where("status",   "==", "Completed")
      .get()
      .then((snap) => {
        setDocs(snap.docs.map((d) => ({ _id: d.id, ...d.data() })));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [vendorId]);

  const months = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 6 }, (_, i) => new Date(now.getFullYear(), now.getMonth() - i, 1));
  }, []);

  const byMonth = useMemo(() => {
    const map = {};
    docs.forEach((b) => {
      const mk = (b.scheduleDate || "").substring(0, 7);
      if (!map[mk]) map[mk] = { revenue: 0, count: 0 };
      map[mk].revenue += parseFloat(b.finalPrice || 0);
      map[mk].count   += 1;
    });
    return map;
  }, [docs]);

  const chartMonths = [...months].reverse();
  const maxRev = chartMonths.reduce((m, d) => Math.max(m, (byMonth[monthKey(d)]?.revenue || 0)), 0);

  if (loading) return <div className="va_loading"><i className="fa fa-spinner fa-pulse"></i></div>;

  return (
    <div className="va_card">
      <div className="va_section_header">
        <i className="fa fa-bar-chart"></i> Monthly Revenue (Last 6 Months)
      </div>
      <div className="va_bar_chart">
        {chartMonths.map((m) => {
          const mk   = monthKey(m);
          const stat = byMonth[mk] || { revenue: 0, count: 0 };
          const pct  = maxRev > 0 ? (stat.revenue / maxRev) * 100 : 0;
          return (
            <div key={mk} className="va_bar_col">
              <div className="va_bar_count">{stat.count}</div>
              <div className="va_bar_fill" style={{ height: `${Math.max(pct, 3)}%` }}></div>
              <div className="va_bar_month">{SHORT_MONTHS[m.getMonth()]}</div>
              <div className="va_bar_year">{m.getFullYear()}</div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        {chartMonths.map((m) => {
          const mk  = monthKey(m);
          const rev = byMonth[mk]?.revenue || 0;
          return (
            <div key={mk} style={{ flex: 1, textAlign: "center", fontSize: 9, color: "#9e9e9e", marginTop: 2 }}>
              {rev > 0 ? toRupee(Math.round(rev)) : "—"}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StylistsTab = ({ vendorId }) => {
  const [stylists, setStylists] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (!vendorId) return;
    return db().collection("stylists")
      .where("vendorId", "==", vendorId)
      .onSnapshot((snap) => {
        setStylists(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      }, () => setLoading(false));
  }, [vendorId]);

  if (loading) return <div className="va_loading"><i className="fa fa-spinner fa-pulse fa-2x"></i></div>;

  return (
    <div>
      <StylistOverviewChart vendorId={vendorId} />
      {stylists.length === 0 ? (
        <div className="va_empty">No stylists found for this vendor.</div>
      ) : (
        stylists.map((st) => (
          <StylistPerformanceCard key={st.id} stylist={st} vendorId={vendorId} />
        ))
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Main VendorAnalytics page
// ═══════════════════════════════════════════════════════════════════════════
const VendorAnalytics = () => {
  const vendorId = getVendorId();
  const [tab, setTab] = useState("overview");

  if (!vendorId) {
    return (
      <div className="va_page va_empty">
        Vendor ID not found. Please log in again.
      </div>
    );
  }

  return (
    <div className="va_page">
      <div className="va_tabs">
        <button
          className={`va_tab${tab === "overview" ? " active" : ""}`}
          onClick={() => setTab("overview")}
        >
          <i className="fa fa-tachometer"></i> Dashboard
        </button>
        <button
          className={`va_tab${tab === "daily" ? " active" : ""}`}
          onClick={() => setTab("daily")}
        >
          <i className="fa fa-calendar-check-o"></i> Daily Revenue
        </button>
        <button
          className={`va_tab${tab === "stylists" ? " active" : ""}`}
          onClick={() => setTab("stylists")}
        >
          <i className="fa fa-cut"></i> Stylists
        </button>
      </div>

      {tab === "overview"  && <DashboardTab    vendorId={vendorId} />}
      {tab === "daily"     && <DailyRevenueTab vendorId={vendorId} />}
      {tab === "stylists"  && <StylistsTab     vendorId={vendorId} />}
    </div>
  );
};

export default VendorAnalytics;
